/*
 * Filename: layout-level-fields-config-controllers.ts
 * Description: layout level fields config controler.
 */

import { CQMasterDataStore } from '../../store/cq-master-data.store';
import { CQAPIService } from '../../services/http-api.service';
import { CQAppStore } from '../../store/cq-app-store';
import { CQAppConstant } from '../../app-constants';
import { TemplateSFObjectStore } from '../../store/models/template-object.model';
import { Utility } from '../../common/utility';

export default class LayoutFieldsConfigController {

    private cqUtility: Utility;
    private readonly cqAPIService: CQAPIService;
    constructor() {
        this.cqUtility = new Utility();
        this.cqAPIService = new CQAPIService();
    }

    /**
     * @param {string} templateName
     * @returns
     * @memberof LayoutFieldsConfigController
     */
    public getLayoutFields(templateName: string) {
        let layoutFields = CQMasterDataStore.OutputConfig.getSelectedLayoutFields(templateName);
        Array.prototype.push.apply(layoutFields,this.getAdditionalFields());
        return layoutFields;
    }

    public getAdditionalFields(){
        return [
            {
                "fieldFullName":"userInfo.Id",
                "fieldLabel":"Current User Id",
                "fieldType":null,
                "referenceTo":"undefined",
                "relationshipName":null,
                "customAddedField":true
            },
            {
                "fieldFullName":"isRecordOwner",
                "fieldLabel":"Record Owner",
                "fieldType":null,
                "referenceTo":"undefined",
                "relationshipName":null,
                "customAddedField":true
            },
            {
                "fieldFullName":"isNewRecord",
                "fieldLabel":"Record",
                "fieldType":null,
                "referenceTo":"undefined",
                "relationshipName":null,
                "customAddedField":true
            },
            {
                "fieldFullName":"isApprover",
                "fieldLabel":"Record",
                "fieldType":null,
                "referenceTo":"undefined",
                "relationshipName":null,
                "customAddedField":true
            },
            {
                description: "Generic setting to store configuration used by ComplianceQuest for various functionality.",
                fieldFullName: "customSettings",
                fieldLabel: "Custom Settings",
                referenceTo: "compliancequest__SQX_Custom_Settings_Public__c",
                relationshipLabel: null,
                relationshipName: "customSettings",
                fieldType: "Lookup",
                customAddedField:true
            }
        ]
    }

    /**
     * @param {*} layout
     * @param {string} fieldName
     * @returns
     * @memberof LayoutFieldsConfigController
     */
    public getFieldDetails(layout: any, fieldName: string) {
        //check if metadata fields has Label. Use fullname as label if not.
        if (layout && layout.metadata.fields[fieldName] && !layout.metadata.fields[fieldName].hasOwnProperty('label')) {
            const spacedFullName = layout.metadata.fields[fieldName].fullName.replace(/([a-z])([A-Z])/g, '$1 $2');
            let newFieldDetails = { ...layout.metadata.fields[fieldName], label: spacedFullName };
            if (!layout.metadata.fields[fieldName].hasOwnProperty('type')) {
                newFieldDetails = { ...newFieldDetails, type: 'Text' };
            }
            return newFieldDetails;
        }
        return layout.metadata.fields[fieldName];
    }

    /**
     * @param {string} selectedTemplateName
     * @param {string} fieldFullName
     * @param {string} itemType type for subheading passed
     * @returns selectedFieldType
     * @memberof LayoutFieldsConfigController
     */
    public getFieldRelatedMetadata(selectedTemplateName: string, fieldFullName: string, itemType?: string) {
        let selectedFieldMetadata: any = {};
        let layoutFields = [];
        let templateType = CQMasterDataStore.OutputConfig.getStoreKeyData(selectedTemplateName, 'templateType')
        if(templateType == 'cqGridGenerator') {
            if(itemType && itemType === 'Child') {
                layoutFields = this.cqUtility.mapField(TemplateSFObjectStore.standardObjectFields.fields);
            } else {
                layoutFields = this.cqUtility.mapField(TemplateSFObjectStore.mainReferenceObjectFields.fields);
            }
        } else {
            layoutFields = this.getLayoutFields(selectedTemplateName);
        }
        
        layoutFields.forEach((field: any) => {
            if (field.fieldFullName === fieldFullName) {
                selectedFieldMetadata = field;
            }
        });
        return selectedFieldMetadata;
    }


    /**
     * @memberof LayoutFieldsConfigController
     */
    public getColumnChunks = (columnSize: number, columnFieldArray: any[]) => {
        const layoutElementsArray: any[] = [];
        const numberOfIterations: number = columnFieldArray.length % columnSize !== 0
            ? Math.floor(columnFieldArray.length % columnSize) + 1
            : columnFieldArray.length / columnSize;

        let copyFieldArray: any = [...columnFieldArray];

        for (let i = 0; i < numberOfIterations; i++) {
            let chunkArray = [];
            chunkArray = copyFieldArray.slice(0, columnSize);
            chunkArray.length > 0 && chunkArray.forEach((chunk: any) => {
                const index = copyFieldArray.indexOf(chunk);
                if (index > -1) {
                    copyFieldArray.splice(index, 1);
                }
            });
            layoutElementsArray.push(chunkArray);
        }
        return layoutElementsArray;
    }

    /**
     * @memberof LayoutFieldsConfigController
     */
    public setFields = (layout: any) => {
        let fields = { 'ComponentLayout': {} };
        layout && layout.layout.layoutSections.forEach((layoutSection: any) => {
            layoutSection.layoutColumns.forEach((column: any) => {
                const fieldDetails = this.getFieldDetails(layout, column.field);
                fields = { ...fields, [fieldDetails && fieldDetails.fullName]: fieldDetails };
            });
        });
        CQMasterDataStore.Details.setFields(fields);
    }

    public getLookupFields = async (lookupObjectName: string) => {
        try {
            CQAppStore.loader.setVisibility(true);
            return this.cqAPIService.getLookupObjectFields(lookupObjectName);
        } catch (error : any) {
            CQAppStore.loader.setVisibility(false);
            const errorObj = {
                errorType: CQAppConstant.ERROR_CUSTOM,
                errorMessage: error.error ? error.error : error.message,
                errorStatus: error.status,
                componentName: ''
            };
            CQAppStore.error.dispatchError(errorObj);
            return [];
        }
    }


    public getLookFieldsDataInEditMode = async (selectedReferenceFields: any[]) => {
        try {
            const getLookupFieldsRequests: any[] = [];
            selectedReferenceFields.forEach((referenceField: any) => {
                getLookupFieldsRequests.push(this.cqAPIService.getLookupObjectFields(referenceField.refernceFieldName));
            });
            if (getLookupFieldsRequests.length > 0) {
                CQAppStore.loader.setVisibility(true);
                await Promise.all(getLookupFieldsRequests).then((response: any) => {
                    response.forEach((res: any, index: number) => {
                        selectedReferenceFields[index].relatedFields = res.fields;
                    });
                    CQAppStore.loader.setVisibility(false);
                }).catch((error : any) => {
                    CQAppStore.loader.setVisibility(false);
                    const errorObj = {
                        errorType: CQAppConstant.ERROR_CUSTOM,
                        errorMessage: error.error ? error.error : error.message,
                        errorStatus: error.status,
                        componentName: ''
                    };
                    CQAppStore.error.dispatchError(errorObj);
                    return [];
                });
                return selectedReferenceFields;
            }
        } catch (error : any) {
            CQAppStore.loader.setVisibility(false);
            const errorObj = {
                errorType: CQAppConstant.ERROR_CUSTOM,
                errorMessage: error.error ? error.error : error.message,
                errorStatus: error.status,
                componentName: ''
            };
            CQAppStore.error.dispatchError(errorObj);
            return [];
        }
    }

    public getPickListValueDataInEditMode = async (objectName:string,fieldWisePicklistValues: any[]) => {
        try {
            const getPickListValueFieldsRequests: any[] = [];
            fieldWisePicklistValues.forEach((field: any) => {
                getPickListValueFieldsRequests.push(this.cqAPIService.getPicklistValues(objectName,field.fieldFullName));
            });
            if (getPickListValueFieldsRequests.length > 0) {
                CQAppStore.loader.setVisibility(true);
                await Promise.all(getPickListValueFieldsRequests).then((response: any) => {
                    response.forEach((res: any, index: number) => {
                        
                        fieldWisePicklistValues[index].pickListValues = res;
                    });
                    CQAppStore.loader.setVisibility(false);
                }).catch((error : any) => {
                    CQAppStore.loader.setVisibility(false);
                    const errorObj = {
                        errorType: CQAppConstant.ERROR_CUSTOM,
                        errorMessage: error.error ? error.error : error.message,
                        errorStatus: error.status,
                        componentName: ''
                    };
                    CQAppStore.error.dispatchError(errorObj);
                    return [];
                });
                return fieldWisePicklistValues;
            }
        } catch (error : any) {
            CQAppStore.loader.setVisibility(false);
            const errorObj = {
                errorType: CQAppConstant.ERROR_CUSTOM,
                errorMessage: error.error ? error.error : error.message,
                errorStatus: error.status,
                componentName: ''
            };
            CQAppStore.error.dispatchError(errorObj);
            return [];
        }
    }

    public async getPicklistValues(objectName: string, fieldName: string) {
        try {
            CQAppStore.loader.setVisibility(true);
            return  await this.cqAPIService.getPicklistValues(objectName, fieldName);
        } catch (error : any) {
            CQAppStore.loader.setVisibility(false);
            const errorObj = {
                errorType: CQAppConstant.ERROR_CUSTOM,
                errorMessage: error.error ? error.error : error.message,
                errorStatus: error.status,
                componentName: ''
            };
            CQAppStore.error.dispatchError(errorObj);
            return [];
        }
    }

    // Picklist values support on rule editor when Picklist field is selected while setting up criteria
    public readonly getPickListValuesFromState = (fieldWisePicklistStateValues: any[], fieldName: string) => {
        
        
        let picklistValues: {label: string, value: string}[] = [];
        if (fieldName) {
            const fieldWisePicklistValues = [...fieldWisePicklistStateValues];
            
            fieldWisePicklistValues.forEach((fieldDetails: any) => {
                if (fieldDetails.fieldFullName === fieldName) {
                    picklistValues = fieldDetails.pickListValues.map((listItem: any) => {
                        return {
                            label: listItem.label,
                            value: listItem.value
                        };
                    }
                    );
                }
            });
        }
        return picklistValues;
    }
    // END : Picklist value support
}

